/**
 * User Database Tab Component
 *
 * Displays all user database fields with copy buttons and edit functionality.
 * Organizes data into logical sections with show/hide toggles for sensitive data.
 */

import { useState } from 'react';
import { Copy, Edit, Eye, EyeOff, Check, LogOut, Loader2, ChevronDown, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useForceSignOutUser, useExpireUserIdToken } from '@/hooks/use-user-management';
import type { User } from '@/types/user-management';

interface UserDatabaseTabProps {
  user: User;
  onEdit: () => void;
}

/**
 * Field Row Component
 * Displays a labeled field with optional copy button
 */
function FieldRow({
  label,
  value,
  canCopy = false,
  isSensitive = false,
}: {
  label: string;
  value: string | null | undefined;
  canCopy?: boolean;
  isSensitive?: boolean;
}) {
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const displayValue = value || 'N/A';
  const maskedValue = '*'.repeat(displayValue.length);

  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({
        title: 'Copied',
        description: `${label} copied to clipboard`,
      });

      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="text-sm font-mono mt-1 break-all">
          {isSensitive && !isVisible ? maskedValue : displayValue}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isSensitive && value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(!isVisible)}
            aria-label={isVisible ? 'Hide value' : 'Show value'}
          >
            {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        )}
        {canCopy && value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            disabled={copied}
            aria-label="Copy to clipboard"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Format Date
 * Formats an ISO date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * User Database Tab
 * Main component that displays all user database fields
 */
export function UserDatabaseTab({ user, onEdit }: UserDatabaseTabProps) {
  const { toast } = useToast();
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const forceSignOut = useForceSignOutUser();
  const [expireIdTokenDialogOpen, setExpireIdTokenDialogOpen] = useState(false);
  const expireIdToken = useExpireUserIdToken();

  const handleConfirmExpireIdToken = async () => {
    try {
      const result = await expireIdToken.mutateAsync(user.id);
      if (result.idTokenRevocationWritten) {
        toast({
          title: 'ID token expired',
          description:
            'Existing idTokens will be rejected on the next authed request. The refresh token is still valid, so the FE can mint new tokens.',
        });
      } else {
        toast({
          title: 'ID token expiry could not be applied',
          description:
            'Backend was reached, but the revocation entry could not be written to Redis. Check Redis health.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to expire ID token',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExpireIdTokenDialogOpen(false);
    }
  };

  const handleConfirmForceSignOut = async () => {
    try {
      const result = await forceSignOut.mutateAsync(user.id);
      if (result.idTokenRevocationWritten) {
        toast({
          title: 'User signed out',
          description: 'Refresh tokens revoked and existing idTokens invalidated.',
        });
      } else {
        toast({
          title: 'Signed out, but idToken revocation could not be written',
          description:
            'Cognito refresh tokens were revoked. Existing idTokens may remain usable until their natural expiry. Check Redis health.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to sign user out',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSignOutDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with action buttons */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">Database Information</h3>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={forceSignOut.isPending || expireIdToken.isPending}
              >
                {(forceSignOut.isPending || expireIdToken.isPending) ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="mr-2 h-4 w-4" />
                )}
                Sign-out Actions
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setSignOutDialogOpen(true)}>
                <LogOut className="mr-2 h-4 w-4" />
                Force Sign Out
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setExpireIdTokenDialogOpen(true)}>
                <Clock className="mr-2 h-4 w-4" />
                Expire ID Token (Keep Refresh)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={onEdit} size="sm">
            <Edit className="mr-2 h-4 w-4" />
            Edit User
          </Button>
        </div>
      </div>

      <AlertDialog open={signOutDialogOpen} onOpenChange={setSignOutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force this user to sign out?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This will revoke every Cognito refresh token for{' '}
                  <span className="font-mono">{user.email ?? user.id}</span> and write a local
                  revocation entry so any existing idToken stops authenticating on the next request.
                </p>
                <p>
                  The user will need to log in again on every device. This cannot be undone, but
                  the user can simply re-authenticate.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forceSignOut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmForceSignOut}
              disabled={forceSignOut.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {forceSignOut.isPending ? 'Signing out…' : 'Force sign out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={expireIdTokenDialogOpen} onOpenChange={setExpireIdTokenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Expire this user's ID token?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Writes a backend revocation entry for{' '}
                  <span className="font-mono">{user.email ?? user.id}</span> so any
                  existing idToken stops authenticating on the next request.
                </p>
                <p>
                  The user's <strong>refresh token stays valid</strong>, so the FE
                  should silently mint new tokens and recover without re-prompting.
                  Use this to test the silent-refresh flow.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={expireIdToken.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExpireIdToken}
              disabled={expireIdToken.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {expireIdToken.isPending ? 'Expiring…' : 'Expire ID token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Basic Information Section */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">Basic Information</h4>
        <div className="rounded-lg border p-4 space-y-1">
          <FieldRow label="User ID" value={user.id} canCopy />
          <FieldRow label="First Name" value={user.firstName} />
          <FieldRow label="Last Name" value={user.lastName} />
          <FieldRow label="Email" value={user.email} canCopy />
          <FieldRow label="Phone" value={user.phone} canCopy />
          <FieldRow label="Date of Birth" value={user.dateOfBirth} />
          <div className="flex items-center justify-between py-2">
            <p className="text-sm font-medium text-muted-foreground">Terms Accepted</p>
            <Badge variant={user.termsAccepted ? 'success' : 'secondary'}>
              {user.termsAccepted ? 'Yes' : 'No'}
            </Badge>
          </div>
        </div>
      </div>

      {/* External Account IDs Section */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">External Account IDs</h4>
        <div className="rounded-lg border p-4 space-y-1">
          <FieldRow label="Olo ID" value={user.oloId} canCopy />
          <FieldRow label="Punchh ID" value={user.punchId} canCopy />
          <FieldRow label="Toast ID" value={user.toastId} canCopy />
          <FieldRow label="Cognito ID" value={user.cognitoId} canCopy />
        </div>
      </div>

      {/* Authentication Tokens Section */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">Authentication Tokens</h4>
        <div className="rounded-lg border p-4 space-y-1">
          {/* Per-platform Olo Auth Tokens */}
          <FieldRow label="Olo Auth Token (iOS)" value={user.oloAuthTokenIos} canCopy isSensitive />
          <FieldRow
            label="Olo Auth Token (Android)"
            value={user.oloAuthTokenAndroid}
            canCopy
            isSensitive
          />
          <FieldRow label="Olo Auth Token (Web)" value={user.oloAuthTokenWeb} canCopy isSensitive />
          <FieldRow
            label="Olo Auth Token (Backend)"
            value={user.oloAuthTokenBackend}
            canCopy
            isSensitive
          />
          <FieldRow label="Punchh Auth Token" value={user.punchhAuthToken} canCopy isSensitive />
        </div>
      </div>

      {/* Push Notification Settings Section */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">Push Notification Settings</h4>
        <div className="rounded-lg border p-4 space-y-1">
          <div className="flex items-center justify-between py-2 border-b">
            <p className="text-sm font-medium text-muted-foreground">Push Notifications Enabled</p>
            <Badge variant={user.pushNotificationsEnabled ? 'success' : 'secondary'}>
              {user.pushNotificationsEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <FieldRow label="Device Type" value={user.deviceType} />
          <FieldRow label="Device ID" value={user.deviceId} canCopy />
          <FieldRow label="GCM Token" value={user.gcmToken} canCopy isSensitive />
          <FieldRow label="APNS Token" value={user.apnsToken} canCopy isSensitive />
          <FieldRow
            label="Last Token Update"
            value={user.lastTokenUpdate ? formatDate(user.lastTokenUpdate) : null}
          />
        </div>
      </div>

      {/* Timestamps Section */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold mb-2">Timestamps</h4>
        <div className="rounded-lg border p-4 space-y-1">
          <FieldRow label="Created At" value={formatDate(user.createdAt)} />
          <FieldRow label="Updated At" value={formatDate(user.updatedAt)} />
        </div>
      </div>
    </div>
  );
}
